<template>
  <v-row>
    <v-col cols="12">
      <v-card class="txn-card" flat>
        <!-- HEADER BAND -->
        <div class="txn-header pl-base-bg">
          <div class="txn-header-icon">
            <v-icon color="#fff" size="22">mdi-pencil</v-icon>
          </div>
          <div>
            <div class="txn-title">Edit Transaction</div>
            <div class="txn-subtitle">{{ form.category || "Update transaction details" }}</div>
          </div>
        </div>

        <!-- FORM -->
        <v-form ref="form" v-model="valid" class="pa-6">
          <div class="txn-section-label">Transaction Details</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.customerName"
                label="Customer / Vendor Name"
                outlined
                dense
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.amount"
                label="Amount"
                type="number"
                outlined
                dense
                :rules="[rules.required]"
                disabled
                prepend-inner-icon="mdi-currency-inr"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                v-model="form.type"
                :items="['INCOME', 'EXPENSE']"
                label="Type"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-swap-horizontal"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.category"
                label="Category"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-tag"
              />
            </v-col>
          </v-row>

          <div class="txn-section-label mt-2">Payment &amp; Notes</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.paymentMethod"
                :items="paymentMethods"
                label="Payment Method"
                outlined
                dense
                prepend-inner-icon="mdi-credit-card"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedDate"
                    label="Date"
                    readonly
                    outlined
                    dense
                    :rules="[rules.required]"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>

                <v-date-picker v-model="form.date" @change="handleDateChange" />
              </v-menu>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="form.note"
                label="Note"
                outlined
                dense
                rows="2"
                prepend-inner-icon="mdi-note-text"
              />
            </v-col>
          </v-row>

          <!-- ACTIONS -->
          <v-row class="mt-4" justify="end" dense>
            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn block color="error" outlined @click="confirmDelete = true">
                Delete
              </v-btn>
            </v-col>

            <v-col cols="4" sm="auto">
              <v-btn block color="secondary" @click="reset">Reset</v-btn>
            </v-col>

            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn
                block
                class="txn-save-btn"
                :loading="loading"
                @click="submit"
              >
                Update
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>

    <global-dialog
      v-if="confirmDelete"
      v-model="confirmDelete"
      :buttons="buttons"
      title="Confirm Delete"
    >
      <p>Are you sure you want to delete this transaction?</p>
    </global-dialog>
  </v-row>
</template>

<script>
import GlobalDialog from "./common/global-dialog.vue";

export default {
  name: "EditTransaction",
  components: { GlobalDialog },

  data() {
    return {
      valid: false,
      loading: false,
      confirmDelete: false,
      dateMenu: false,

      form: {
        customerName: "",
        amount: "",
        type: "",
        category: "",
        note: "",
        paymentMethod: "CASH",
        date: "",
      },

      paymentMethods: [
        "CASH",
        "BANK_TRANSFER",
        "UPI",
        "DEBIT_CARD",
        "CREDIT_CARD",
        "CHEQUE",
        "NET_BANKING",
        "WALLET",
        "OTHER",
      ],

      rules: {
        required: (v) => !!v || "Required",
      },
    };
  },

  async created() {
    const id = this.$route.params.id;
    await this.loadTransaction(id);
  },

  computed: {
    formattedDate() {
      if (!this.form.date) return "";

      const date = new Date(this.form.date);

      return date.toLocaleDateString("en-GB", {
        timeZone: "Asia/Kolkata",
      });
    },

    buttons() {
      return [
        {
          text: "Cancel",
          action: () => (this.confirmDelete = false),
          outlined: true,
          color: "#0a5a90",
        },
        { text: "Delete", action: this.delete, color: "error" },
      ];
    },
  },

  methods: {
    handleDateChange(val) {
      this.form.date = val;
      this.dateMenu = false;
    },

    async loadTransaction(id) {
      try {
        await this.$store.dispatch("transactions/getTransaction", id);

        const transaction = this.$store.state.transactions.transaction;

        this.form = {
          ...transaction,
          customerName: transaction.customerName,
          amount: transaction.amount,
          type: transaction.type,
          category: transaction.category,
          note: transaction.note,
          paymentMethod: transaction.paymentMethod,
          date: transaction.date
            ? new Date(transaction.date).toLocaleDateString("en-CA", {
                timeZone: "Asia/Kolkata",
              })
            : "",
        };
      } catch (err) {
        this.$toast?.error("Failed to load transaction");
        this.$router.push("/transactions");
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      try {
        const id = this.$route.params.id;

        await this.$store.dispatch("transactions/updateTransaction", {
          id,
          payload: this.form,
        });

        this.$toast?.success("Transaction updated successfully");
        this.$router.push(`/transactions/${this.form._id}/view`);
      } catch (err) {
        this.$toast?.error("Failed to update transaction");
      } finally {
        this.loading = false;
      }
    },

    async delete() {
      const id = this.$route.params.id;

      try {
        await this.$store.dispatch("transactions/deleteTransaction", id);

        this.$toast?.success("Transaction deleted successfully");
        this.$router.push("/transactions");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to delete transaction",
        );
      } finally {
        this.confirmDelete = false;
      }
    },

    reset() {
      this.$refs.form.reset();
    },
  },
};
</script>

<style scoped>
.txn-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.txn-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.txn-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.txn-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.txn-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.txn-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.txn-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.v-menu__content {
  z-index: 9999 !important;
}
</style>
